import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import Cropper, { Area, Point } from 'react-easy-crop';
import { BoutiqueItems } from '../types';
import API from '../lib/API';
import { updateBoutiqueItem as updateBoutiqueItemInStore } from '../slices/boutiquesSlice';
import { getCroppedImageBlob } from '../lib/cropImage';
import {
    deleteBoutiqueItemPhoto,
    MAX_BOUTIQUE_ITEM_PHOTOS,
    uploadBoutiqueItemPhoto,
    urlsMatch,
} from '../lib/boutiqueItemImages';
import ConfirmationModal from './ConfirmationModal';

import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import Spinner from 'react-bootstrap/Spinner';

const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;
const ITEM_IMAGE_ASPECT = 1;
const CROPPED_IMAGE_SIZE = 1200;

interface BoutiqueItemImageModalProps {
    show: boolean;
    handleClose: () => void;
    token: string;
    boutiqueId: number;
    item?: BoutiqueItems;
}

const parseItemImageUrls = (images: BoutiqueItems['images'] | undefined): string[] => {
    if (!images) {
        return [];
    }

    return images.map((url) => url.trim()).filter(Boolean);
};

const BoutiqueItemImageModal: React.FC<BoutiqueItemImageModalProps> = ({
    show,
    handleClose,
    token,
    boutiqueId,
    item,
}) => {
    const dispatch = useDispatch();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const isUploadingRef = useRef(false);
    const isDeletingRef = useRef(false);
    const [imageUrls, setImageUrls] = useState<string[]>([]);
    const [uploading, setUploading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [error, setError] = useState('');
    const [imageUrlToDelete, setImageUrlToDelete] = useState<string | null>(null);
    const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
    const [pendingFileName, setPendingFileName] = useState('');
    const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

    useEffect(() => {
        if (!show) {
            setImageUrls([]);
            setError('');
            return;
        }

        if (!item || isUploadingRef.current || isDeletingRef.current) {
            return;
        }

        setImageUrls(parseItemImageUrls(item.images));
        setError('');
    }, [show, item]);

    const getErrorMessage = (err: unknown): string => {
        if (err instanceof Error) {
            return err.message;
        }
        if (typeof err === 'string') {
            return err;
        }
        return 'Failed to save photo';
    };

    const isBusy = uploading || deleting;

    useEffect(() => {
        return () => {
            if (cropImageSrc) {
                URL.revokeObjectURL(cropImageSrc);
            }
        };
    }, [cropImageSrc]);

    const clearCropSelection = () => {
        if (cropImageSrc) {
            URL.revokeObjectURL(cropImageSrc);
        }
        setCropImageSrc(null);
        setPendingFileName('');
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setCroppedAreaPixels(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const validateFile = (file: File): string | null => {
        if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
            return 'Please select a JPEG, PNG, WebP, or GIF image';
        }
        if (file.size > MAX_FILE_SIZE_BYTES) {
            return 'Image must be 5 MB or smaller';
        }
        return null;
    };

    const handleUpload = async (file: File) => {
        if (!item) return;

        if (imageUrls.length >= MAX_BOUTIQUE_ITEM_PHOTOS) {
            setError(`Maximum of ${MAX_BOUTIQUE_ITEM_PHOTOS} photos allowed per item`);
            return;
        }

        isUploadingRef.current = true;
        setUploading(true);
        setError('');

        try {
            const uploadedPhoto = await uploadBoutiqueItemPhoto(
                boutiqueId,
                item.boutiqueItemId,
                file
            );

            const updatedImageUrls = [...imageUrls, uploadedPhoto.url];

            const updatedItem = await API.updateBoutiqueItem(token, item.boutiqueItemId, {
                name: item.name,
                description: item.description,
                itemPrice: item.itemPrice,
                itemCurrency: item.itemCurrency,
                discount: item.discount,
                boutiqueId,
                images: updatedImageUrls,
            });

            dispatch(updateBoutiqueItemInStore(updatedItem));
            const parsedImages = parseItemImageUrls(updatedItem.images);
            setImageUrls(parsedImages.length > 0 ? parsedImages : updatedImageUrls);
            clearCropSelection();
        } catch (err) {
            setError(getErrorMessage(err));
        } finally {
            isUploadingRef.current = false;
            setUploading(false);
        }
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }

        const validationError = validateFile(file);
        if (validationError) {
            setError(validationError);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            return;
        }

        if (cropImageSrc) {
            URL.revokeObjectURL(cropImageSrc);
        }

        setError('');
        setCropImageSrc(URL.createObjectURL(file));
        setPendingFileName(file.name);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setCroppedAreaPixels(null);
    };

    const handleDeleteImageClick = (imageUrl: string) => {
        setImageUrlToDelete(imageUrl);
    };

    const handleDeleteImageCancel = () => {
        setImageUrlToDelete(null);
    };

    const handleDeleteImageConfirm = async () => {
        if (!item || !imageUrlToDelete) return;

        const urlToDelete = imageUrlToDelete;
        setImageUrlToDelete(null);
        isDeletingRef.current = true;
        setDeleting(true);
        setError('');

        try {
            await deleteBoutiqueItemPhoto(
                boutiqueId,
                item.boutiqueItemId,
                urlToDelete
            );

            const updatedImageUrls = imageUrls.filter((url) => !urlsMatch(url, urlToDelete));

            const updatedItem = await API.updateBoutiqueItem(token, item.boutiqueItemId, {
                name: item.name,
                description: item.description,
                itemPrice: item.itemPrice,
                itemCurrency: item.itemCurrency,
                discount: item.discount,
                boutiqueId,
                images: updatedImageUrls,
            });

            dispatch(updateBoutiqueItemInStore(updatedItem));
            setImageUrls(parseItemImageUrls(updatedItem.images));
        } catch (err) {
            setError(getErrorMessage(err));
        } finally {
            isDeletingRef.current = false;
            setDeleting(false);
        }
    };

    const handleCropUpload = async () => {
        if (!cropImageSrc || !croppedAreaPixels) {
            setError('Adjust the crop before uploading');
            return;
        }

        const mimeType = pendingFileName.toLowerCase().endsWith('.png')
            ? 'image/png'
            : 'image/jpeg';
        const extension = mimeType === 'image/png' ? 'png' : 'jpg';

        try {
            const croppedBlob = await getCroppedImageBlob(
                cropImageSrc,
                croppedAreaPixels,
                CROPPED_IMAGE_SIZE,
                mimeType
            );
            const croppedFile = new File(
                [croppedBlob],
                `cropped-${pendingFileName.replace(/\.[^.]+$/, '')}.${extension}`,
                { type: mimeType }
            );

            await handleUpload(croppedFile);
        } catch (err) {
            setError(getErrorMessage(err));
        }
    };

    const atPhotoLimit = imageUrls.length >= MAX_BOUTIQUE_ITEM_PHOTOS;

    return (
        <>
        <ConfirmationModal
            show={imageUrlToDelete !== null}
            title="Delete image?"
            message="Are you sure you want to delete this image? This cannot be undone."
            onConfirm={handleDeleteImageConfirm}
            onCancel={handleDeleteImageCancel}
        />
        <Modal show={show} onHide={handleClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    {item ? `Images — ${item.name}` : 'Item Images'}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <Alert variant="danger">{error}</Alert>}

                <div className="d-flex justify-content-between align-items-center mb-3">
                    <span className="text-muted">
                        {imageUrls.length}/{MAX_BOUTIQUE_ITEM_PHOTOS} photos
                    </span>
                    {atPhotoLimit && (
                        <span className="text-warning">Maximum photo limit reached</span>
                    )}
                </div>

                {imageUrls.length > 0 ? (
                    <div className="d-flex flex-wrap gap-3 mb-4">
                        {imageUrls.map((url, index) => (
                            <div key={`${url}-${index}`} className="text-center">
                                <div className="position-relative d-inline-block">
                                    <img
                                        src={url}
                                        alt={`${item?.name ?? 'Item'} ${index + 1}`}
                                        className="rounded border"
                                        style={{
                                            width: '120px',
                                            height: '120px',
                                            objectFit: 'cover',
                                        }}
                                    />
                                    <Button
                                        variant="danger"
                                        size="sm"
                                        className="position-absolute top-0 end-0 m-1"
                                        onClick={() => handleDeleteImageClick(url)}
                                        disabled={isBusy}
                                    >
                                        Delete
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-muted mb-4">No photos uploaded yet.</p>
                )}

                {cropImageSrc && (
                    <div className="mb-4">
                        <Form.Label>Crop photo (1:1 ratio)</Form.Label>
                        <div
                            className="rounded border mb-3"
                            style={{ position: 'relative', height: '320px', backgroundColor: '#212529' }}
                        >
                            <Cropper
                                image={cropImageSrc}
                                crop={crop}
                                zoom={zoom}
                                aspect={ITEM_IMAGE_ASPECT}
                                onCropChange={setCrop}
                                onZoomChange={setZoom}
                                onCropComplete={(_, pixels) => setCroppedAreaPixels(pixels)}
                            />
                        </div>
                        <Form.Group className="mb-3">
                            <Form.Label>Zoom</Form.Label>
                            <Form.Range
                                min={1}
                                max={3}
                                step={0.05}
                                value={zoom}
                                onChange={(event) => setZoom(Number(event.target.value))}
                                disabled={isBusy}
                            />
                        </Form.Group>
                        <div className="d-flex gap-2">
                            <Button
                                variant="primary"
                                onClick={handleCropUpload}
                                disabled={isBusy || !croppedAreaPixels}
                            >
                                {uploading ? 'Uploading...' : 'Crop & upload'}
                            </Button>
                            <Button
                                variant="secondary"
                                onClick={clearCropSelection}
                                disabled={isBusy}
                            >
                                Cancel
                            </Button>
                        </div>
                    </div>
                )}

                {!cropImageSrc && (
                    <Form.Group>
                        <Form.Label>Upload photo</Form.Label>
                        <Form.Control
                            ref={fileInputRef}
                            type="file"
                            accept={ACCEPTED_IMAGE_TYPES.join(',')}
                            onChange={handleFileChange}
                            disabled={isBusy || atPhotoLimit}
                        />
                        <Form.Text className="text-muted">
                            JPEG, PNG, WebP, or GIF up to 5 MB. Images are cropped to a square before upload.
                            Maximum {MAX_BOUTIQUE_ITEM_PHOTOS} photos per item.
                        </Form.Text>
                    </Form.Group>
                )}
            </Modal.Body>
            <Modal.Footer>
                {isBusy && (
                    <span className="me-auto text-muted d-flex align-items-center gap-2">
                        <Spinner animation="border" size="sm" />
                        {deleting ? 'Deleting...' : 'Uploading...'}
                    </span>
                )}
                <Button variant="secondary" onClick={handleClose} disabled={isBusy}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
        </>
    );
};

export default BoutiqueItemImageModal;
